/**
 * Reads a newline-delimited JSON stream (`application/x-ndjson`) and yields one
 * parsed value per line, in order.
 *
 * A chunk can end anywhere — mid-line or even mid-character — so bytes are
 * decoded in streaming mode and a line is parsed only once its newline (or the
 * end of the stream) has arrived. Blank lines are skipped (servers use them as
 * keep-alives). A line that is not valid JSON throws: a protocol that silently
 * drops an event is harder to debug than one that fails loudly.
 *
 * Stopping early (`break`, a thrown error in the loop body) cancels the
 * underlying stream, which aborts the request instead of leaving it running.
 */
export async function* readNdjson<T = unknown>(
  stream: ReadableStream<Uint8Array>
): AsyncGenerator<T, void, undefined> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let finished = false;

  try {
    while (!finished) {
      const { done, value } = await reader.read();
      finished = done;
      buffer += done
        ? decoder.decode()
        : decoder.decode(value, { stream: true });

      const lines = buffer.split("\n");
      buffer = finished ? "" : (lines.pop() ?? "");

      for (const line of lines) {
        if (line.trim()) yield JSON.parse(line) as T;
      }
    }
  } finally {
    if (!finished) await reader.cancel().catch(() => undefined);
    reader.releaseLock();
  }
}
